import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useEffect, useMemo, useState } from "react";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { InboxEmailModal } from "@components/modals/InboxEmailModal";
import Icon1 from "@assets/icons/Icon";
import SearchField from "@components/ui/textfields/SearchField";
import { colors } from "sexy";
import { SyncEmail } from "types/sync_email";
import { getEmailBodyText } from "@utils/emailBody";
import { getProjectDisplayName } from "types";
import { accessibleProjectIdsFor } from "@helpers/accessHelpers";
import { Timestamp } from "firebase/firestore";

// ─── Date helper ──────────────────────────────────────────────────────────────

function formatEmailDate(ts: Timestamp): string {
  const date = new Date(ts.seconds * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 1 && date.getDate() === now.getDate()) {
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (diffDays < 7) {
    return date.toLocaleDateString("fr-FR", { weekday: "short" });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  }
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

// ─── Filter tabs ──────────────────────────────────────────────────────────────

type FilterTab = "all" | "unread" | "unmatched";

function FilterTabButton({
  tab,
  label,
  active,
  count,
  onClick,
}: {
  tab: FilterTab;
  label: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
        active
          ? "bg-black text-white"
          : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
      }`}
    >
      {label}
      {count !== undefined && count > 0 && (
        <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-xs">
          {count}
        </span>
      )}
    </button>
  );
}

// ─── Email row ────────────────────────────────────────────────────────────────

function InboxEmailRow({
  email,
  projectName,
  onClick,
}: {
  email: SyncEmail;
  projectName: string | null;
  onClick: () => void;
}) {
  const bodyPreview = getEmailBodyText(email).replace(/\n/g, " ").trim();
  const attachmentCount = email.attachments?.length || 0;

  return (
    <div
      onClick={onClick}
      className={`group flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer transition-colors ${
        email.is_read ? "bg-white hover:bg-neutral-50" : "bg-blue-50/30 hover:bg-blue-50/50"
      }`}
    >
      {/* Unread indicator */}
      <div className="flex-shrink-0 w-2">
        {!email.is_read && (
          <div className="w-2 h-2 rounded-full bg-blue-500" />
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 gap-0.5">
        {/* Sender + subject on one line */}
        <div className="flex flex-row items-baseline gap-2 min-w-0">
          <span
            className={`text-sm truncate shrink-0 max-w-48 ${
              email.is_read ? "text-neutral-700" : "font-semibold text-neutral-900"
            }`}
          >
            {email.from_name || email.from_email}
          </span>
          <span
            className={`text-sm truncate ${
              email.is_read ? "text-neutral-400" : "font-medium text-neutral-700"
            }`}
          >
            {email.subject || "(sans objet)"}
          </span>
        </div>
        {/* Preview */}
        <div className="text-xs text-neutral-400 truncate">
          {bodyPreview || "—"}
        </div>
      </div>

      {/* Right side: project badge + attachment + date */}
      <div className="flex flex-row items-center gap-3 flex-shrink-0">
        {projectName ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 max-w-32 truncate border border-blue-200">
            {projectName}
          </span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-400 max-w-32 truncate">
            Sans projet
          </span>
        )}
        {attachmentCount > 0 && (
          <div className="flex flex-row items-center gap-1 text-neutral-400">
            <Icon1 name="join" size={14} color={colors.neutral_400} />
            <span className="text-xs">{attachmentCount}</span>
          </div>
        )}
        <span className="text-xs text-neutral-400 w-12 text-right">
          {email.received_at ? formatEmailDate(email.received_at) : "—"}
        </span>
      </div>
    </div>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function InboxScreen() {
  const inboxEmails = useProjectStore((s) => s.inboxEmails);
  const isLoadingInbox = useProjectStore((s) => s.isLoadingInbox);
  const inboxUnreadCount = useProjectStore((s) => s.inboxUnreadCount);
  const fetchInboxEmails = useProjectStore((s) => s.fetchInboxEmails);
  const fetchUnreadCount = useProjectStore((s) => s.fetchUnreadCount);
  const projects = useProjectStore((s) => s.projects);
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);

  const [selectedEmail, setSelectedEmail] = useState<SyncEmail | null>(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");

  useEffect(() => {
    if (orgData?.id) {
      fetchInboxEmails(orgData.id);
      fetchUnreadCount(orgData.id);
    }
  }, []);

  // Belt-and-braces frontend scoping. Non-admins only see inbox emails
  // attached to projects they can access; unmatched emails (no project_id
  // yet) are hidden from non-admins since they could be from outside the
  // user's scope.
  const accessibleProjectIds = useMemo(
    () => accessibleProjectIdsFor(userData, projects),
    [userData, projects],
  );
  const scopedEmails = useMemo(() => {
    if (accessibleProjectIds === "all") return inboxEmails;
    return inboxEmails.filter(
      (e) => e.project_id && accessibleProjectIds.has(e.project_id),
    );
  }, [inboxEmails, accessibleProjectIds]);

  const filtered = useMemo(() => {
    let emails = [...scopedEmails].sort(
      (a, b) => (b.received_at?.seconds || 0) - (a.received_at?.seconds || 0),
    );

    if (activeFilter === "unread") {
      emails = emails.filter((e) => !e.is_read);
    } else if (activeFilter === "unmatched") {
      emails = emails.filter((e) => !e.project_id);
    }

    const q = search.toLowerCase().trim();
    if (q) {
      emails = emails.filter(
        (e) =>
          e.subject?.toLowerCase().includes(q) ||
          e.from_email?.toLowerCase().includes(q) ||
          e.from_name?.toLowerCase().includes(q) ||
          getEmailBodyText(e).toLowerCase().includes(q) ||
          getProjectName(e.project_id)?.toLowerCase().includes(q),
      );
    }

    return emails;
  }, [scopedEmails, search, activeFilter, projects]);

  const unreadCount = scopedEmails.filter((e) => !e.is_read).length;
  const unmatchedCount = scopedEmails.filter((e) => !e.project_id).length;

  function getProjectName(projectId?: string): string | null {
    if (!projectId) return null;
    const project = projects.find((p) => p.id === projectId);
    if (!project) return null;
    return getProjectDisplayName(project);
  }

  return (
    <div className="flex flex-col overflow-auto max-h-screen">
      {selectedEmail && (
        <InboxEmailModal
          onClose={() => {
            setSelectedEmail(null);
            // Refresh counts after viewing
            if (orgData?.id) fetchUnreadCount(orgData.id);
          }}
          email={selectedEmail}
        />
      )}

      <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10">
        <div className="flex flex-col w-full max-w-1k gap-4">
          {/* Header */}
          <div className="flex flex-row w-full justify-between items-start">
            <div className="flex flex-col w-full max-w-400">
              <Text1>Boîte de réception</Text1>
              <Text2>Emails reçus en réponse à vos envois.</Text2>
            </div>
          </div>

          {/* Search + filters */}
          <SearchField
            placeholder="Rechercher par expéditeur, sujet ou projet…"
            value={search}
            onChange={(v) => setSearch(v)}
          />
          <div className="flex flex-row gap-2">
            <FilterTabButton
              tab="all"
              label="Tous"
              active={activeFilter === "all"}
              onClick={() => setActiveFilter("all")}
            />
            <FilterTabButton
              tab="unread"
              label="Non lus"
              active={activeFilter === "unread"}
              count={unreadCount}
              onClick={() => setActiveFilter("unread")}
            />
            <FilterTabButton
              tab="unmatched"
              label="Sans projet"
              active={activeFilter === "unmatched"}
              count={unmatchedCount}
              onClick={() => setActiveFilter("unmatched")}
            />
          </div>

          {/* List */}
          <div className="flex flex-col rounded-lg border border-neutral-200 overflow-hidden bg-white">
            {/* List header */}
            <div className="flex flex-row items-center gap-3 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
              <span className="text-xs text-neutral-500 flex-1">
                {filtered.length} email{filtered.length !== 1 ? "s" : ""}
                {search ? " trouvé" + (filtered.length !== 1 ? "s" : "") : ""}
              </span>
            </div>

            {/* Rows */}
            {isLoadingInbox && inboxEmails.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <div className="animate-spin h-6 w-6 border-2 border-neutral-300 border-t-black rounded-full" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Icon1 name="inbox" size={32} color={colors.neutral_300} />
                <span className="text-sm text-neutral-400">
                  {search
                    ? "Aucun email ne correspond à la recherche."
                    : "Aucun email reçu."}
                </span>
              </div>
            ) : (
              filtered.map((email) => (
                <InboxEmailRow
                  key={email.id}
                  email={email}
                  projectName={getProjectName(email.project_id)}
                  onClick={() => setSelectedEmail(email)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
